/**
 * SelfRouteReportButton — Çalışan Kontrol Paneli'nde "Bugünkü Turumu Bildir" butonu.
 *
 * Şub 16, 2026 — Feature flag `employee_self_route_report` ile gate'lenir.
 * 2 aşamalı modal:
 *   1) Müşteri seçimi (db.customers)
 *   2) Form doldur (Start/End KM, Start/End Time, Plaka)
 *
 * Günde 1 kayıt: `existing_today` context'ten gelir → varsa buton "Bugün Bildirildi ✓"
 * durumuna geçer, tıklanınca detay gösterir.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Truck, Send, X, CheckCircle2, Loader2, ArrowLeft, Paperclip, Upload, ImageIcon, FileText, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';

import api from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { useFeatureFlag } from '../lib/useFeatureFlag';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';

const CUSTOMER_COLORS = {
  fedex: 'bg-purple-100 text-purple-700 border-purple-300',
  bpost: 'bg-amber-100 text-amber-700 border-amber-300',
  dhl:   'bg-yellow-100 text-yellow-800 border-yellow-300',
  dpd:   'bg-red-100 text-red-700 border-red-300',
  postnl: 'bg-orange-100 text-orange-700 border-orange-300',
  getax: 'bg-slate-100 text-slate-700 border-slate-300',
};

const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Şub 16, 2026 — Müşteri tanımına göre dinamik render edilecek "driver-input" alanları.
// Backend'de db.customers.fields listesinden bu whitelist ile filtrelenir.
// Financial / admin-only alanlar (hourly_rate, start_cost, totaal_excl_btw, route_number) burada YOK.
const CUSTOMER_EXTRA_FIELDS = {
  copilot: { type: 'text', label: 'Yardımcı Şoför', placeholder: 'Ad Soyad', icon: 'UserPlus' },
  break_time: { type: 'time', label: 'Mola Süresi (HH:MM)', placeholder: '00:30' },
  rit: { type: 'select', label: 'Rit', optionsKey: 'rit_options' },
  route_type: { type: 'select', label: 'Rota Tipi', optionsKey: 'route_type_options' },
  stops: { type: 'number', label: 'Durak Sayısı', placeholder: '0' },
};

export default function SelfRouteReportButton({ compact = false }) {
  const enabled = useFeatureFlag('employee_self_route_report');
  const { user } = useAuthStore();
  const isEmployee = user?.role === 'EMPLOYEE';
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({
    start_km: '', end_km: '', start_time: '', end_time: '',
    license_plate: '', route_code: '', notes: '',
  });
  const [extraFields, setExtraFields] = useState({}); // {copilot: '', break_time: '00:30', ...}
  const [attachments, setAttachments] = useState([]); // [{dataUrl, name, size, type}]
  const [plateComboOpen, setPlateComboOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const loadCtx = async () => {
    setLoadingCtx(true);
    try {
      const res = await api.get('/planning/self-report/context');
      setCtx(res.data?.data);
      setForm((f) => ({ ...f, license_plate: res.data?.data?.default_plate || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Yüklenemedi');
      setOpen(false);
    } finally { setLoadingCtx(false); }
  };

  const [hiddenBySS, setHiddenBySS] = useState(false);
  useEffect(() => {
    if (!enabled || !isEmployee) return;
    api.get('/planning/my-todays-assignment')
      .then((r) => setHiddenBySS((r.data?.data || []).some((a) => a.is_today && a.start_stop?.hide_route_report)))
      .catch(() => {});
  }, [enabled, isEmployee]);

  useEffect(() => {
    if (open) {
      loadCtx();
      setStep(1);
      setCustomer(null);
      setAttachments([]);
      setExtraFields({});
    }
  }, [open]);

  const alreadyToday = !!ctx?.existing_today;

  const workHoursPreview = useMemo(() => {
    const s = form.start_time, e = form.end_time;
    if (!s || !e) return null;
    try {
      const [sh, sm] = s.split(':').map(Number);
      const [eh, em] = e.split(':').map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins < 0) mins += 24 * 60;
      return (mins / 60).toFixed(2);
    } catch { return null; }
  }, [form.start_time, form.end_time]);

  const canSubmit = customer && form.start_km !== '' && form.end_km !== '' &&
    form.start_time && form.end_time && (form.license_plate || '').trim();

  const addFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    const arr = Array.from(files);
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(`En fazla ${MAX_ATTACHMENTS} dosya ekleyebilirsiniz`);
      return;
    }
    const toProcess = arr.slice(0, remaining);
    if (arr.length > remaining) toast.info(`${arr.length - remaining} dosya limit nedeniyle atlandı`);
    setUploadingCount((n) => n + toProcess.length);
    const results = [];
    for (const file of toProcess) {
      try {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`"${file.name}" ${MAX_FILE_SIZE_MB}MB'den büyük — atlandı`);
          continue;
        }
        const type = file.type || '';
        const okType = ACCEPTED_MIME.includes(type) || type.startsWith('image/');
        if (!okType) {
          toast.error(`"${file.name}" desteklenmeyen tür — atlandı`);
          continue;
        }
        const dataUrl = await readAsDataURL(file);
        results.push({ dataUrl, name: file.name, size: file.size, type });
      } catch (e) {
        toast.error(`"${file.name}" okunamadı`);
      }
    }
    setUploadingCount((n) => Math.max(0, n - toProcess.length));
    if (results.length) {
      setAttachments((prev) => [...prev, ...results]);
      toast.success(`${results.length} dosya eklendi`);
    }
  }, [attachments.length]);

  const removeAttachment = (idx) => setAttachments((p) => p.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Boş string / undefined extra alanları at
      const cleanedExtras = Object.fromEntries(
        Object.entries(extraFields || {}).filter(([, v]) => v !== '' && v != null)
      );
      await api.post('/planning/self-report', {
        customer_slug: customer.slug,
        start_km: Number(form.start_km),
        end_km: Number(form.end_km),
        start_time: form.start_time,
        end_time: form.end_time,
        license_plate: form.license_plate.trim(),
        route_code: form.route_code.trim() || null,
        notes: form.notes.trim() || null,
        attachments: attachments.map((a) => a.dataUrl),
        extra_fields: Object.keys(cleanedExtras).length ? cleanedExtras : null,
      });
      toast.success('Bildirim gönderildi — admin onayına iletildi');
      setOpen(false);
      // Reload context to reflect the "already today" state
      loadCtx();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gönderilemedi');
    } finally { setSubmitting(false); }
  };

  if (!enabled || !isEmployee || hiddenBySS) return null;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className={
          compact
            ? "bg-amber-500 hover:bg-amber-600 text-white gap-1.5 h-8 px-3 text-xs font-semibold rounded-full shadow-sm hover:shadow-md transition-all"
            : "bg-amber-500 hover:bg-amber-600 text-white gap-2 w-full sm:w-auto"
        }
        data-testid="self-report-open-btn"
        title="Bugünkü Turumu Bildir"
      >
        <Truck className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        {compact ? "Turu Bildir" : "Bugünkü Turumu Bildir"}
      </Button>

      {open && (
        <div className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="self-report-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="font-semibold flex items-center gap-2">
                {step === 2 && !alreadyToday && (
                  <button onClick={() => setStep(1)} className="text-slate-400 hover:text-slate-700" data-testid="self-report-back-btn">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <Truck className="h-4 w-4 text-emerald-600" />
                {alreadyToday ? 'Bugünkü Bildirim' : step === 1 ? 'Müşteri Seç' : `${customer?.name || ''} — Form`}
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 py-4">
              {loadingCtx ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : alreadyToday ? (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 className="h-5 w-5" />
                    Bugün için bildirim gönderildi
                  </div>
                  <div className="rounded border border-slate-200 dark:border-slate-700 p-3 space-y-1 text-xs">
                    <div><span className="text-slate-500">Tarih:</span> <span className="font-mono">{ctx?.today_date}</span></div>
                    <div><span className="text-slate-500">Başlangıç KM:</span> <span className="font-mono">{ctx?.existing_today?.answers?.start_km ?? '—'}</span></div>
                    <div><span className="text-slate-500">Bitiş KM:</span> <span className="font-mono">{ctx?.existing_today?.answers?.end_km ?? '—'}</span></div>
                    <div><span className="text-slate-500">Saat:</span> <span className="font-mono">{ctx?.existing_today?.answers?.start_time || '—'} → {ctx?.existing_today?.answers?.end_time || '—'}</span></div>
                    <div><span className="text-slate-500">Plaka:</span> <span className="font-mono font-semibold">{ctx?.existing_today?.answers?.license_plate || '—'}</span></div>
                  </div>
                  {(ctx?.existing_today?.attachments || []).length > 0 && (
                    <div className="space-y-1.5" data-testid="self-report-existing-attachments">
                      <div className="text-xs text-slate-500 flex items-center gap-1.5">
                        <Paperclip className="h-3.5 w-3.5" />
                        Ekler ({ctx.existing_today.attachments.length})
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {ctx.existing_today.attachments.map((att) => (
                          <AttachmentThumb key={att.id} att={att} />
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500">
                    Değişiklik gerekiyorsa admin ile iletişime geçin. Günde 1 bildirim gönderilebilir.
                  </p>
                </div>
              ) : step === 1 ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 mb-2">Bugün çalıştığınız müşteriyi seçin:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(ctx?.customers || []).map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => {
                          setCustomer(c);
                          // Şub 16, 2026 — start_time hafızadan, end_time şu anki saat.
                          setForm((f) => {
                            const d = new Date();
                            const hh = String(d.getHours()).padStart(2, '0');
                            const mm = String(d.getMinutes()).padStart(2, '0');
                            const nowStr = `${hh}:${mm}`;
                            return {
                              ...f,
                              start_time: f.start_time || (ctx?.default_start_time || ''),
                              end_time: f.end_time || nowStr,
                            };
                          });
                          // Şub 16, 2026 — Müşteriye özel ek alanların default değerlerini yükle.
                          const dv = c.default_values || {};
                          const seed = {};
                          for (const k of Object.keys(CUSTOMER_EXTRA_FIELDS)) {
                            if ((c.fields || []).includes(k) && dv[k] !== undefined) {
                              seed[k] = String(dv[k] ?? '');
                            }
                          }
                          setExtraFields(seed);
                          setStep(2);
                        }}
                        className={`px-3 py-3 rounded border-2 text-sm font-semibold transition-all hover:scale-[1.02] ${CUSTOMER_COLORS[c.slug] || 'bg-slate-50 text-slate-700 border-slate-300'}`}
                        data-testid={`self-report-customer-${c.slug}`}
                      >
                        {c.name || c.label || c.slug}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500">Başlangıç KM *</span>
                      <input type="number" inputMode="numeric" value={form.start_km} onChange={(e) => setForm((f) => ({ ...f, start_km: e.target.value }))} className="border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700" data-testid="self-report-start-km" placeholder="12500" />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500">Bitiş KM *</span>
                      <input type="number" inputMode="numeric" value={form.end_km} onChange={(e) => setForm((f) => ({ ...f, end_km: e.target.value }))} className="border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700" data-testid="self-report-end-km" placeholder="12800" />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5">
                        Başlama Saati *
                        {form.start_time && ctx?.default_start_time && form.start_time === ctx.default_start_time && (
                          <Badge variant="outline" className="text-[9px]">hafızadan</Badge>
                        )}
                      </span>
                      <input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700" data-testid="self-report-start-time" />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5">
                        Bitiş Saati *
                        {form.end_time && (() => {
                          const d = new Date();
                          const now = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                          return form.end_time === now ? (
                            <Badge variant="outline" className="text-[9px]">şimdi</Badge>
                          ) : null;
                        })()}
                      </span>
                      <input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700" data-testid="self-report-end-time" />
                    </label>
                    <label className="flex flex-col gap-1 col-span-2">
                      <span className="text-xs text-slate-500 flex items-center gap-2">
                        Araç Plakası *
                        {ctx?.default_plate && form.license_plate === ctx.default_plate && (
                          <Badge variant="outline" className="text-[9px]">profilinizden geldi</Badge>
                        )}
                      </span>
                      {/* Popover + Command combobox (Yeni Route Ekle modalıyla aynı) */}
                      <Popover open={plateComboOpen} onOpenChange={setPlateComboOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={plateComboOpen}
                            className="w-full justify-between font-normal"
                            data-testid="self-report-plate-btn"
                          >
                            <span className={form.license_plate ? 'font-mono font-semibold' : 'text-slate-400 font-normal'}>
                              {form.license_plate || 'Plaka arayın veya seçin…'}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[320px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Plaka ara (ör. 1-ABC)…" data-testid="self-report-plate-search" />
                            <CommandList>
                              <CommandEmpty>Plaka bulunamadı</CommandEmpty>
                              <CommandGroup>
                                {(ctx?.available_vehicles || []).map((v) => (
                                  <CommandItem
                                    key={v.id || v.license_plate}
                                    value={v.license_plate}
                                    onSelect={() => {
                                      setForm((f) => ({ ...f, license_plate: v.license_plate }));
                                      setPlateComboOpen(false);
                                    }}
                                    data-testid={`self-report-plate-option-${v.license_plate}`}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${form.license_plate === v.license_plate ? 'opacity-100' : 'opacity-0'}`} />
                                    <div className="flex flex-col">
                                      <span className="font-mono font-medium">{v.license_plate}</span>
                                      {(v.brand || v.model) && (
                                        <span className="text-[10px] text-slate-500">{v.brand} {v.model}</span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Ya da elle girin:
                      </p>
                      <input
                        type="text"
                        autoComplete="off"
                        value={form.license_plate}
                        onChange={(e) => setForm((f) => ({ ...f, license_plate: e.target.value.toUpperCase() }))}
                        className="border rounded px-2 py-1.5 font-mono uppercase dark:bg-slate-800 dark:border-slate-700"
                        data-testid="self-report-plate"
                        placeholder="1-ABC-123"
                      />
                      {ctx?.available_vehicles?.length > 0 && (
                        <span className="text-[10px] text-slate-400">
                          {ctx.available_vehicles.length} aktif araç sistemden öneriliyor
                        </span>
                      )}
                    </label>
                    <label className="flex flex-col gap-1 col-span-2">
                      <span className="text-xs text-slate-500">Rota Kodu (opsiyonel)</span>
                      <input type="text" value={form.route_code} onChange={(e) => setForm((f) => ({ ...f, route_code: e.target.value }))} className="border rounded px-2 py-1.5 font-mono dark:bg-slate-800 dark:border-slate-700" data-testid="self-report-route-code" placeholder="LM-01" />
                    </label>

                    {/* Şub 16, 2026 — Müşteri tanımına göre dinamik ek alanlar */}
                    {Object.keys(CUSTOMER_EXTRA_FIELDS)
                      .filter((k) => (customer?.fields || []).includes(k))
                      .map((k) => {
                        const cfg = CUSTOMER_EXTRA_FIELDS[k];
                        const value = extraFields[k] ?? '';
                        const setVal = (v) => setExtraFields((p) => ({ ...p, [k]: v }));
                        const cls = "border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700";
                        const testId = `self-report-extra-${k}`;
                        let control;
                        if (cfg.type === 'select') {
                          const opts = (customer?.field_options || {})[cfg.optionsKey] || [];
                          control = (
                            <select value={value} onChange={(e) => setVal(e.target.value)} className={cls} data-testid={testId}>
                              <option value="">— seçin —</option>
                              {opts.map((o) => (<option key={o} value={o}>{o}</option>))}
                            </select>
                          );
                        } else if (cfg.type === 'time') {
                          control = (
                            <input type="time" value={value} onChange={(e) => setVal(e.target.value)} className={cls} data-testid={testId} />
                          );
                        } else if (cfg.type === 'number') {
                          control = (
                            <input type="number" inputMode="numeric" value={value} onChange={(e) => setVal(e.target.value)} className={cls} data-testid={testId} placeholder={cfg.placeholder} />
                          );
                        } else {
                          control = (
                            <input type="text" value={value} onChange={(e) => setVal(e.target.value)} className={cls} data-testid={testId} placeholder={cfg.placeholder} />
                          );
                        }
                        return (
                          <label key={k} className="flex flex-col gap-1 col-span-2 sm:col-span-1">
                            <span className="text-xs text-slate-500">{cfg.label}</span>
                            {control}
                          </label>
                        );
                      })}

                    <label className="flex flex-col gap-1 col-span-2">
                      <span className="text-xs text-slate-500">Notlar</span>
                      <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="border rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700" rows={2} data-testid="self-report-notes" />
                    </label>

                    {/* --- Dosya Ekleri (Şub 16, 2026) --- */}
                    <div className="col-span-2 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 flex items-center gap-1.5">
                          <Paperclip className="h-3.5 w-3.5" />
                          Dosya Ekleri <span className="text-[10px] text-slate-400">(opsiyonel · max {MAX_ATTACHMENTS} · {MAX_FILE_SIZE_MB}MB/dosya)</span>
                        </span>
                        {attachments.length > 0 && (
                          <span className="text-[10px] font-mono text-slate-500">{attachments.length}/{MAX_ATTACHMENTS}</span>
                        )}
                      </div>

                      <div
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onDragEnter={onDragOver}
                        onDragLeave={onDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        className={`rounded-lg border-2 border-dashed transition-all cursor-pointer p-4 text-center select-none
                          ${dragActive
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                            : 'border-slate-300 dark:border-slate-700 hover:border-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                        data-testid="self-report-dropzone"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                      >
                        <Upload className={`h-6 w-6 mx-auto mb-1 ${dragActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div className="text-xs text-slate-600 dark:text-slate-300">
                          <span className="hidden sm:inline">Dosyayı buraya sürükleyin veya </span>
                          <span className="text-emerald-600 font-medium underline">seçmek için tıklayın</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          JPG · PNG · WEBP · HEIC · PDF
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*,.pdf,.heic,.heif"
                          className="hidden"
                          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                          data-testid="self-report-file-input"
                        />
                      </div>

                      {/* Mobile-friendly camera capture button */}
                      <div className="flex sm:hidden">
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 active:scale-[0.98] transition"
                          data-testid="self-report-camera-btn"
                        >
                          <ImageIcon className="h-4 w-4" />
                          Kamera ile Fotoğraf Çek
                        </button>
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />
                      </div>

                      {uploadingCount > 0 && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {uploadingCount} dosya işleniyor…
                        </div>
                      )}

                      {attachments.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1" data-testid="self-report-attachments-grid">
                          {attachments.map((att, idx) => {
                            const isImg = (att.type || '').startsWith('image/');
                            return (
                              <div
                                key={idx}
                                className="relative group aspect-square rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-50 dark:bg-slate-800"
                                data-testid={`self-report-attachment-${idx}`}
                              >
                                {isImg ? (
                                  <img src={att.dataUrl} alt={att.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 p-1">
                                    <FileText className="h-6 w-6" />
                                    <span className="text-[9px] mt-1 truncate w-full text-center px-1">{att.name}</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); removeAttachment(idx); }}
                                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition"
                                  aria-label="Sil"
                                  data-testid={`self-report-attachment-remove-${idx}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">
                                  {(att.size / 1024).toFixed(0)} KB
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  {form.start_km && form.end_km && Number(form.end_km) >= Number(form.start_km) && (
                    <div className="text-[11px] text-slate-500 flex items-center gap-3">
                      <span>Toplam KM: <b className="font-mono text-slate-800 dark:text-slate-200">{Number(form.end_km) - Number(form.start_km)}</b></span>
                      {workHoursPreview !== null && <span>Çalışma: <b className="font-mono text-slate-800 dark:text-slate-200">{workHoursPreview} sa</b></span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!alreadyToday && step === 2 && (
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="self-report-cancel-btn">Geri</Button>
                <Button
                  onClick={submit}
                  disabled={!canSubmit || submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="self-report-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Gönder
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Şub 16, 2026 — Attachment thumbnail: <img> Bearer header gönderemediği için
// authenticated fetch → blob URL yaklaşımı kullanıyoruz.
function AttachmentThumb({ att }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [errored, setErrored] = React.useState(false);
  const isImg = (att.content_type || '').startsWith('image');

  React.useEffect(() => {
    let alive = true;
    let currentUrl = null;
    if (isImg) {
      (async () => {
        try {
          const res = await api.get(`/planning/form-attachments/${att.id}/download?preview=1`, {
            responseType: 'blob',
          });
          if (!alive) return;
          currentUrl = URL.createObjectURL(res.data);
          setBlobUrl(currentUrl);
        } catch (_e) {
          if (alive) setErrored(true);
        }
      })();
    }
    return () => {
      alive = false;
      if (currentUrl) { try { URL.revokeObjectURL(currentUrl); } catch (_e) { /* ignore */ } }
    };
  }, [att.id, isImg]);

  const openInNewTab = async () => {
    try {
      const res = await api.get(`/planning/form-attachments/${att.id}/download?preview=1`, {
        responseType: 'blob',
      });
      const u = URL.createObjectURL(res.data);
      window.open(u, '_blank', 'noopener');
      // Blob URL 60s sonra release
      setTimeout(() => { try { URL.revokeObjectURL(u); } catch (_e) { /* ignore */ } }, 60000);
    } catch (_e) {
      toast.error('Ek açılamadı');
    }
  };

  return (
    <button
      type="button"
      onClick={openInNewTab}
      className="aspect-square rounded border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-50 dark:bg-slate-800 flex items-center justify-center relative"
      data-testid={`self-report-attachment-thumb-${att.id}`}
      title={att.file_name}
    >
      {isImg && blobUrl && !errored ? (
        <img src={blobUrl} alt={att.file_name} className="w-full h-full object-cover" />
      ) : isImg && !errored ? (
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      ) : (
        <div className="flex flex-col items-center justify-center p-1">
          <FileText className="h-6 w-6 text-slate-400" />
          <span className="text-[9px] mt-1 truncate w-full text-center px-1">{att.file_name}</span>
        </div>
      )}
    </button>
  );
}
